import type {
  OrderLineTypeFilter,
  OrderScriptRow,
} from "../types/marketplace.types";

export const ORDER_TYPES = ["script", "brief", "mixed"] as const;

export type OrderType = (typeof ORDER_TYPES)[number];

export const ORDER_LINE_TYPE_FILTER_OPTIONS = ["all", "script", "brief"] as const;

export type OrderLineTypeFilterOption =
  (typeof ORDER_LINE_TYPE_FILTER_OPTIONS)[number];

export const ORDER_LINE_TYPE_COPY: Record<
  OrderLineTypeFilter,
  { key: string; fallback: string }
> = {
  script: { key: "marketplace.orders.lineType.script", fallback: "Script" },
  brief: { key: "marketplace.orders.lineType.brief", fallback: "Brief" },
};

export function toOrderLineTypeFilter(
  option: OrderLineTypeFilterOption,
): OrderLineTypeFilter | undefined {
  return option === "all" ? undefined : option;
}

type OrderLineTypeFields = Pick<OrderScriptRow, "briefId" | "customAssetKind">;

/** The snapshot shape never decides the type; the list endpoints filter on these two columns. */
export function resolveOrderLineType(row: OrderLineTypeFields): OrderLineTypeFilter {
  return row.customAssetKind === "BRIEF" || row.briefId ? "brief" : "script";
}

export function orderLineMatchesTypeFilter(
  row: OrderLineTypeFields,
  option: OrderLineTypeFilterOption,
): boolean {
  return option === "all" || resolveOrderLineType(row) === option;
}

export function orderLineIsBrief(row: OrderLineTypeFields): boolean {
  return resolveOrderLineType(row) === "brief";
}

export function orderContainsBriefLines(
  rows: ReadonlyArray<OrderScriptRow> | undefined,
): boolean {
  return Boolean(rows?.some((row) => orderLineIsBrief(row)));
}

export function countOrderLineKinds(rows: ReadonlyArray<OrderLineTypeFields>): {
  scripts: number;
  briefs: number;
  total: number;
} {
  let scripts = 0;
  let briefs = 0;
  for (const row of rows) {
    if (orderLineIsBrief(row)) briefs += 1;
    else scripts += 1;
  }
  return { scripts, briefs, total: rows.length };
}

export function resolveOrderType(rows: ReadonlyArray<OrderLineTypeFields>): OrderType {
  const { scripts, briefs } = countOrderLineKinds(rows);
  if (briefs === 0) return "script";
  return scripts === 0 ? "brief" : "mixed";
}

/** Ops can copy Nexus persona and legacy Opportunity scripts; uploads and brief lines have no editable source. */
export function canDuplicateOrderLine(
  row: Pick<OrderScriptRow, "scriptId" | "briefId" | "sourceFormat" | "customAssetKind" | "script">,
): boolean {
  const source = row.script?.source;
  return (
    Boolean(row.scriptId) &&
    !row.briefId &&
    (row.sourceFormat ?? "NEXUS") === "NEXUS" &&
    row.customAssetKind == null &&
    (source === "persona" || source === "legacy")
  );
}
